import { useQuery } from '@apollo/client';
import {
  Autocomplete,
  TextField,
  createFilterOptions,
  type AutocompleteProps,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useAppContext } from '~/contexts';
import { AutocompleteGroupHeader, AutocompleteGroupItems } from '..';
import { AvailableViewsDocument } from './ViewAutocomplete.generated';

export interface ViewAutocompleteProps
  extends Omit<AutocompleteProps<number, false, false, false>, 'options' | 'renderInput'> {
  error?: boolean;
  helperText?: React.ReactNode;
  name?: string;
}

export const ViewAutocomplete = ({
  error,
  helperText,
  name,
  ...autocompleteProps
}: ViewAutocompleteProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data, loading } = useQuery(AvailableViewsDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const viewsById = useMemo(() => {
    return new Map(data?.network?.views.map((view) => [view.id, view] as const) ?? []);
  }, [data?.network?.views]);

  const options = useMemo(() => {
    const views = [...viewsById.values()];
    views.sort((a, b) => {
      const showSort = a.show.name.localeCompare(b.show.name);
      return showSort !== 0 ? showSort : a.name.localeCompare(b.name);
    });
    return views.map(({ id }) => id);
  }, [viewsById]);

  if (loading && !data?.network?.views) return null;

  const getOptionLabel = (option: number) => {
    const view = viewsById.get(option);
    return view ? `${view.name} (${view.show.name})` : '';
  };

  const filterOptions = createFilterOptions({
    stringify: (option: number) => {
      const view = viewsById.get(option);
      return view ? `${view.name} ${view.show.name}` : '';
    },
  });

  return (
    <Autocomplete<number>
      fullWidth
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      groupBy={(option) => viewsById.get(option)?.show.id.toString() ?? ''}
      loading={loading}
      options={options}
      renderGroup={(params) => {
        const show = data?.network?.views.find((view) => String(view.show.id) === params.group)
          ?.show;
        return (
          <li key={params.key}>
            <AutocompleteGroupHeader>{show?.name}</AutocompleteGroupHeader>
            <AutocompleteGroupItems>{params.children}</AutocompleteGroupItems>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          name={name}
          {...namedByPlaceholder(
            params,
            t('pages.device_signals.settings.placeholders.select_view'),
          )}
        />
      )}
      renderOption={(props, option) => <li {...props}>{viewsById.get(option)?.name}</li>}
      {...autocompleteProps}
    />
  );
};
